// This file contains the JSA Tab component for the DEL. We will use this to render the different JSA components for the DEL.

import React from "react";
// Core Components
import { JsaBasicInfoTab } from "./core/jsaBasicInfoComponents";
import { JsaRacInfoTab } from "./core/jsaRacInfoComponents";
import { JsaTrainingInfoTab } from "./core/jsaTrainingInfoComponents";
import { JsaSignatureInfoTab } from "./core/jsaSignatureInfoComponents";
// V1 Components
import { JsaEmergencyEquipmentInfoTab } from "./v1/jsaEmergencyEquipmentInfoComponents";
import { JsaPermitInfoTab } from "./v1/jsaPermitInfoComponents";
import { JsaPpeInfoTab } from "./v1/jsaPpeInfoComponents";
import { JsaHazComInfoTab } from "./v1/jsaHazComInfoComponents";
import { JsaConfinedSpaceInfoTab } from "./v1/jsaConfinedSpaceInfoComponents";
import { JsaFallProtectionInfoTab } from "./v1/jsaFallProtectionInfoComponents";
// V2 Components
import { JsaV2HazardsTab } from "./v2/jsaV2HazardsComponents";
import { JsaV2CmInfoTab } from "./v2/jsaV2CmComponents";
import styles from "./JsaTab.module.css";

// JSA Tab Buttons
export const JsaTabButtons = ({ tabName, callbacks }) => {
  // Setting the active JSA tab
  const handleSetActiveJsaTab = () => {
    callbacks.baseActions.setActiveJsaTab(tabName);
  };
  return (
    <button
      className={`${styles.tabButton} ${
        callbacks.jsaBase.activeJsaTab === tabName ? styles.active : ""
      }`}
      onClick={handleSetActiveJsaTab}
    >
      {tabName}
    </button>
  );
};

// All JSA Components for Version 1
const AllJsaV1Components = ({ callbacks }) => {
  return (
    <div className={styles.allJsaContent}>
      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Basic Information</h2>
        <JsaBasicInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Emergency Equipment</h2>
        <JsaEmergencyEquipmentInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Permits</h2>
        <JsaPermitInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>JHA</h2>
        <JsaRacInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>PPE</h2>
        <JsaPpeInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>HazCom</h2>
        <JsaHazComInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Confined Space</h2>
        <JsaConfinedSpaceInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Fall Protection</h2>
        <JsaFallProtectionInfoTab callbacks={callbacks} />
      </div>
      {!callbacks.samplingEventForm && (
        <div className={styles.jsaComponentSection}>
          <h2 className={styles.sectionTitle}>Training</h2>
          <JsaTrainingInfoTab callbacks={callbacks} />
        </div>
      )}

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Signatures</h2>
        <JsaSignatureInfoTab callbacks={callbacks} />
      </div>
    </div>
  );
};

// All JSA Components for Version 2
const AllJsaV2Components = ({ callbacks }) => {
  return (
    <div className={styles.allJsaContent}>
      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Basic Information</h2>
        <JsaBasicInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>JHA</h2>
        <JsaRacInfoTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Hazards</h2>
        <JsaV2HazardsTab callbacks={callbacks} />
      </div>

      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Control Measures</h2>
        <JsaV2CmInfoTab callbacks={callbacks} />
      </div>

      {!callbacks.samplingEventForm && (
        <div className={styles.jsaComponentSection}>
          <h2 className={styles.sectionTitle}>Training</h2>
          <JsaTrainingInfoTab callbacks={callbacks} />
        </div>
      )}
      <div className={styles.jsaComponentSection}>
        <h2 className={styles.sectionTitle}>Signatures</h2>
        <JsaSignatureInfoTab callbacks={callbacks} />
      </div>
    </div>
  );
};

// Individual JSA Components, without the H2 element
const IndividualJsaComponents = ({ Component, callbacks }) => {
  return (
    <div className={styles.jsaComponentSection}>
      <Component callbacks={callbacks} />
    </div>
  );
};

// JSA Tab
export const JsaTab = ({
  jsaProps,
  actions,
  commit,
  samplingEventForm = false,
}) => {
  // Set isEmbeddedInForm state if passed through props
  React.useEffect(() => {
    if (jsaProps.isEmbeddedInForm === true) {
      actions.base.setIsEmbeddedInForm(true);
    } else if (jsaProps.isEmbeddedInForm === false) {
      actions.base.setIsEmbeddedInForm(false);
    }
  }, [jsaProps.isEmbeddedInForm, actions.base]);

  const jsaState = {
    // Data from the application
    allEmployees: jsaProps.allEmployees,
    delJsaEmployeeList: jsaProps.delJsaEmployeeList || [],
    // JSA Data
    jsaData: jsaProps.jsaData,
    jsaMode: jsaProps.jsaMode || "edit", // Default to edit mode if not provided
    jsaVersion: jsaProps.jsaVersion,
    jsaBase: jsaProps.jsaBase,
    jsaCore: jsaProps.jsaCore,
    jsaV1: jsaProps.jsaV1,
    jsaV2: jsaProps.jsaV2,
    // Actions
    baseActions: actions.base,
    coreActions: actions.core,
    v1Actions: actions.v1,
    v2Actions: actions.v2,
    // Optional commit bundle used by Sampling Event form to autosave on blur/change
    commit,
    // Optional trainingTabRequired used by Sampling Event form to hide the training tab
    samplingEventForm,

    // Autosave passed to the JsaTab from other forms that the JSA is attached to. This can be updated in the future or just kept as is
    emitPatch: jsaProps.emitPatch,

    // Autosave errors state and actions for displaying validation errors in JSA fields
    autosaveErrors: jsaProps.autosaveErrors || { hasErrors: false, fieldErrors: {}, rowErrors: {} },
    autosaveErrorsActions: actions.autosaveErrors,
  };

  // Setting JSA Version 1 or 2 based on jsaVersion. Showing the correct tabs
  if (jsaState.jsaVersion === 1) {
    return (
      <div className={styles.jsaTabContainer}>
        <div className={styles.jsaTabMenuBar}>
          <JsaTabButtons tabName="All" callbacks={jsaState} />
          <JsaTabButtons tabName="Basic Info" callbacks={jsaState} />
          <JsaTabButtons tabName="Emergency Equipment" callbacks={jsaState} />
          <JsaTabButtons tabName="Permits" callbacks={jsaState} />
          <JsaTabButtons tabName="JHA" callbacks={jsaState} />
          <JsaTabButtons tabName="PPE" callbacks={jsaState} />
          <JsaTabButtons tabName="HazCom" callbacks={jsaState} />
          <JsaTabButtons tabName="Confined Space" callbacks={jsaState} />
          <JsaTabButtons tabName="Fall Protection" callbacks={jsaState} />
          {!samplingEventForm && (
            <JsaTabButtons tabName="Training" callbacks={jsaState} />
          )}
          <JsaTabButtons tabName="Signatures" callbacks={jsaState} />
        </div>
        {/* JSA Tab Content */}
        <div className={styles.jsaTabContent}>
          {(() => {
            switch (jsaState.jsaBase.activeJsaTab) {
              case "All":
                return <AllJsaV1Components callbacks={jsaState} />;
              case "Basic Info":
                return (
                  <IndividualJsaComponents
                    Component={JsaBasicInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "Emergency Equipment":
                return (
                  <IndividualJsaComponents
                    Component={JsaEmergencyEquipmentInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "Permits":
                return (
                  <IndividualJsaComponents
                    Component={JsaPermitInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "JHA":
                return (
                  <IndividualJsaComponents
                    Component={JsaRacInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "PPE":
                return (
                  <IndividualJsaComponents
                    Component={JsaPpeInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "HazCom":
                return (
                  <IndividualJsaComponents
                    Component={JsaHazComInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "Confined Space":
                return (
                  <IndividualJsaComponents
                    Component={JsaConfinedSpaceInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "Fall Protection":
                return (
                  <IndividualJsaComponents
                    Component={JsaFallProtectionInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "Training":
                if (!samplingEventForm) {
                  return (
                    <IndividualJsaComponents
                      Component={JsaTrainingInfoTab}
                      callbacks={jsaState}
                    />
                  );
                }
                return null;
              case "Signatures":
                return (
                  <IndividualJsaComponents
                    Component={JsaSignatureInfoTab}
                    callbacks={jsaState}
                  />
                );
              default:
                return null;
            }
          })()}
        </div>
      </div>
    );
  } else {
    return (
      <div className={styles.jsaTabContainer}>
        <div className={styles.jsaTabMenuBar}>
          <JsaTabButtons
            tabName="All"
            activeTab={jsaState.jsaBase.activeJsaTab}
            callbacks={jsaState}
          />
          <JsaTabButtons
            tabName="Basic Info"
            activeTab={jsaState.jsaBase.activeJsaTab}
            callbacks={jsaState}
          />
          <JsaTabButtons
            tabName="JHA"
            activeTab={jsaState.jsaBase.activeJsaTab}
            callbacks={jsaState}
          />
          <JsaTabButtons
            tabName="Hazards"
            activeTab={jsaState.jsaBase.activeJsaTab}
            callbacks={jsaState}
          />
          <JsaTabButtons
            tabName="Control Measures"
            activeTab={jsaState.jsaBase.activeJsaTab}
            callbacks={jsaState}
          />
          {!samplingEventForm && (
            <JsaTabButtons
              tabName="Training"
              activeTab={jsaState.jsaBase.activeJsaTab}
              callbacks={jsaState}
            />
          )}
          <JsaTabButtons
            tabName="Signatures"
            activeTab={jsaState.jsaBase.activeJsaTab}
            callbacks={jsaState}
          />
        </div>
        {/* JSA Tab Content */}
        <div className={styles.jsaTabContent}>
          {(() => {
            switch (jsaState.jsaBase.activeJsaTab) {
              case "All":
                return <AllJsaV2Components callbacks={jsaState} />;
              case "Basic Info":
                return (
                  <IndividualJsaComponents
                    Component={JsaBasicInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "JHA":
                return (
                  <IndividualJsaComponents
                    Component={JsaRacInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "Hazards":
                return (
                  <IndividualJsaComponents
                    Component={JsaV2HazardsTab}
                    callbacks={jsaState}
                  />
                );
              case "Control Measures":
                return (
                  <IndividualJsaComponents
                    Component={JsaV2CmInfoTab}
                    callbacks={jsaState}
                  />
                );
              case "Training":
                if (!samplingEventForm) {
                  return (
                    <IndividualJsaComponents
                      Component={JsaTrainingInfoTab}
                      callbacks={jsaState}
                    />
                  );
                }
                return null;
              case "Signatures":
                return (
                  <IndividualJsaComponents
                    Component={JsaSignatureInfoTab}
                    callbacks={jsaState}
                  />
                );
              default:
                return null;
            }
          })()}
        </div>
      </div>
    );
  }
};
